import {
  FinalFee,
  FinalFeeType,
  FinalProduct,
  FinanceResponseData,
  Insurance,
  InsuranceType,
  OtherCharge,
} from '../financingTypes';
import { ProductCategory } from '../penTypes';
import { TradeIn } from '../dealTypes';

// Form Elements
export enum FormElementEnum {
  FORM_TITLE = 'formTitle',
  SECTION = 'section',
  SUBSECTION = 'subsection',
  FEE_INPUT = 'feeInput',
  PRICE_INPUT = 'priceInput',
  STRING_INPUT = 'stringInput',
  INTEGER_INPUT = 'integerInput',
  CHARGE_TABLE = 'chargeTable',
  FEE_TABLE = 'feeTable',
  PRODUCT_TABLE = 'productTable',
  INSURANCE_TABLE = 'insuranceTable',
  TRADE_IN_INPUT = 'tradeInInput',
  FEE_DROPDOWN = 'feeDropdown',
  LINE_ITEM = 'lineItem',
}

export type OtherChargeFields =
  | 'otherNonTaxableCharges'
  | 'otherDownPayments'
  | 'otherTaxableCharges'
  | 'carDefects';

export type ColumnType = {
  title?: string;
  width: string;
};

// Defines the parameters for a column in a FeeTable
export type FeeColumnType = {
  fieldName: keyof FinalFee;
} & ColumnType;

// Defines the parameters for a column in a ProductTable
export type ProductColumnType = {
  fieldName: keyof FinalProduct;
} & ColumnType;

// Defines the parameters for a column in a ProductTable
export type InsuranceColumnType = {
  fieldName: keyof Insurance;
} & ColumnType;

// Defines the parameters for a column in a ProductTable
export type OtherChargeColumnType = {
  fieldName: keyof OtherCharge;
} & ColumnType;

export type SumValueType = {
  responseDataFields?: (keyof FinanceResponseData)[]; // List of fields to add from responseData
  feeTypes?: FinalFeeType[]; // List of fee types to add from responseData.finalizedFeeList
  productCategories?: (ProductCategory | undefined)[]; // List of product categories to add from responseData.finalizedProductList
  otherCharges?: OtherChargeFields[]; // List of charge fields to add from responseData
  insuranceTypes?: InsuranceType[]; // List of insurance types to add from responseData.insurances
  downPaymentPayoff?: boolean; // whether to add the downpayment payoff (negative downpayment) amount to sum.
  downPaymentWithMinZero?: boolean; // whether to add the downpayment (Min zero).
  subtractDownPaymentWithMinZero?: boolean; // whether to subtract the total downpayment (Min zero)
};

// Stores form title
export type FormTitleType = {
  type: FormElementEnum.FORM_TITLE;
  title?: string;
};

// Element to describe common fields across all form elements
export type BaseFormElementType = {
  type: FormElementEnum;
  title?: string;
  subtitle?: string;
  required?: boolean;
};

// Stores basic line item with a value
export type LineItemType = {
  type: FormElementEnum.LINE_ITEM;
  value?: string;
  sumValue?: SumValueType;
  responseDataField?: keyof FinanceResponseData;
} & BaseFormElementType;

// Describes a section of the form, including any title definitions
export type FormSectionType = {
  type: FormElementEnum.SECTION;
  value?: string;
  sumValue?: SumValueType;
  sectionElements?: FormElementType[];
  options?: {
    onlyShowForUsedCar?: boolean;
  };
} & BaseFormElementType;

// Describes a subsection within a section
export type FormSubsectionType = {
  type: FormElementEnum.SUBSECTION;
  value?: string;
  sumValue?: SumValueType;
  subSectionElements?: FormElementType[];
} & BaseFormElementType;

// Single input for Fee types that are contained in finalizedFeeList
export type FeeInputType = {
  type: FormElementEnum.FEE_INPUT;
  feeType: FinalFeeType;
} & BaseFormElementType;

// Single input for price field
export type PriceInputType = {
  type: FormElementEnum.PRICE_INPUT;
  responseDataField: keyof FinanceResponseData;
} & BaseFormElementType;

// Single input for string field
export type StringInputType = {
  type: FormElementEnum.STRING_INPUT;
  responseDataField: keyof FinanceResponseData;
} & BaseFormElementType;

// Single input for integer field
export type IntegerInputType = {
  type: FormElementEnum.INTEGER_INPUT;
  responseDataField: keyof FinanceResponseData;
} & BaseFormElementType;

// Displays a table of charges. Often used for displaying downpayments and nontaxable charges
export type ChargeTableType = {
  type: FormElementEnum.CHARGE_TABLE;
  responseDataField: OtherChargeFields;
  columns: OtherChargeColumnType[];
  displayTotalValue: boolean; // whether to display total amount value across all charges
  options?: {
    hideAmountInputs?: boolean; // whether to display amount input for a charge.
  };
} & BaseFormElementType;

// Displays a table of fees with custom inputs and formatting
export type FeeTableType = {
  type: FormElementEnum.FEE_TABLE;
  feeType: FinalFeeType;
  columns: FeeColumnType[];
  displayTotalValue?: boolean;
} & BaseFormElementType;

// Displays a table of products with custom inputs and formatting
export type ProductTableType = {
  type: FormElementEnum.PRODUCT_TABLE;
  productCategory: ProductCategory;
  columns: ProductColumnType[];
  displayTotalValue?: boolean;
} & BaseFormElementType;

// Displays a table of insurances with custom inputs and formatting
export type InsuranceTableType = {
  type: FormElementEnum.INSURANCE_TABLE;
  insuranceTypes: InsuranceType[];
  columns: InsuranceColumnType[];
  displayTotalValue?: boolean;
} & BaseFormElementType;

// Describes a trade in field
export type TradeInItemType = {
  type: FormElementEnum.TRADE_IN_INPUT;
  tradeInField: keyof TradeIn | 'netTrade';
  value?: string;
  sumValue?: SumValueType;
} & BaseFormElementType;

// Describes a trade in field
export type FeeDropdownType = {
  type: FormElementEnum.FEE_DROPDOWN;
  feeTypes: FinalFeeType[];
  value?: string | number;
} & BaseFormElementType;

export type FormElementType =
  | FormTitleType
  | LineItemType
  | FormSectionType
  | FormSubsectionType
  | FeeInputType
  | PriceInputType
  | StringInputType
  | IntegerInputType
  | ChargeTableType
  | FeeTableType
  | ProductTableType
  | InsuranceTableType
  | TradeInItemType
  | FeeDropdownType;

// Describes metadata fields which are used to populate default values for the RIC form
export type RICMetadata = {
  feeTypeList: FinalFeeType[]; // list of fees in the schema
  numDefects?: number; // number of defects to be displayed on RIC form
  numInsuranceCreditDisability?: number;
  numInsuranceCreditLife?: number;
  numGapProducts?: number;
  numOtherDownpaymentElements?: number;
  numOtherFees?: number;
  numOtherNonTaxableCharges?: number;
  numOtherTaxableCharges?: number;
  numServiceContracts?: number;
  numSurfaceProtectionProducts?: number;
  numTheftDeterrentDevices?: number;
  numTradeIns?: number;
  rebateAsOtherCharge?: boolean; // Most RIC forms don't have a distinct rebate field. This will determine whether to display rebate as an "Other Downpayment"
  downPaymentAsOtherCharge?: boolean; // Some states (PA) should send its base cash down payment as an "Other Charge" so that we can pass a description string along with it.
};

export type RICSchema = {
  metadata?: RICMetadata;
  elements: FormElementType[];
};
